import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "@mui/material";
import { IContactResponseItem } from "shared";
import { When } from "react-if";

import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { WrappedLink } from "../../WrappedLink";

type ParentContactLinkProps = Pick<IContactResponseItem, `parentContactId` | `parentContactName` | `parentContactArchived`>;

/** Link to the parent, shown under a child contact's name */
export function ParentContactLink({ parentContactId, parentContactName, parentContactArchived }: ParentContactLinkProps) {
  if (!parentContactId) return null;

  return (
    <span className={ `flex items-center gap-1` }>
      <Tooltip title={ `Parent contact` }>
        <span>
          <WrappedLink to={ `/contacts/${parentContactId}` }>
            <Paragraph
              variant={ `link` }
              as={ `span` }
              size={ `xs` }
            >
              { `↳ ${parentContactName}` }
            </Paragraph>
          </WrappedLink>
        </span>
      </Tooltip>
      <When condition={ parentContactArchived }>
        <Tooltip title={ `The parent contact is archived, so statements for this contact are not being sent.` }>
          <ExclamationTriangleIcon className={ `h-3.5 w-3.5 flex-none text-yellow-400` } />
        </Tooltip>
      </When>
    </span>
  );
}

/** Badge on a parent contact that opens a list of its children */
export function ChildContactsPopover({ childContacts }: Pick<IContactResponseItem, `childContacts`>) {
  if (!childContacts?.length) return null;

  return (
    // Stop the click reaching the grid cell, which would toggle row selection
    <span onClick={ e => e.stopPropagation() }>
      <Popover className={ `relative inline-flex` }>
        <PopoverButton className={ `rounded-md whitespace-nowrap mt-0.5 mr-2 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-blue-50 text-blue-700 ring-blue-600/20 hover:bg-blue-100 focus:outline-none cursor-pointer` }>
          { `${childContacts.length} ${childContacts.length === 1 ? `child` : `children`}` }
        </PopoverButton>
        <PopoverPanel
          anchor={ `bottom start` }
          className={ `z-20 w-64 rounded-lg border border-gray-200 bg-white shadow-xl [--anchor-gap:6px]` }
        >
          <div className={ `px-3 py-2 border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500` }>
            { `Child contacts` }
          </div>
          <div className={ `max-h-64 overflow-y-auto py-1` }>
            { childContacts.map(child => (
              <div
                key={ child.id }
                className={ `px-3 py-1` }
              >
                <WrappedLink to={ `/contacts/${child.id}` }>
                  <Paragraph
                    variant={ `link` }
                    as={ `span` }
                  >
                    { child.name }
                  </Paragraph>
                </WrappedLink>
              </div>
            )) }
          </div>
        </PopoverPanel>
      </Popover>
    </span>
  );
}
